import React, { useState } from "react";
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  useTheme,
  Tooltip,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import StarIcon from "@mui/icons-material/Star";
import { SystemSection, useSystemsContext } from "../../context/SystemsContext";

const tabOptions: SystemSection[] = [
  "assetProcessing",
  "assetSyndication",
  "assetStorage",
  "clientProcess",
];

const AccordionPanel: React.FC = () => {
  const theme = useTheme();

  const {
    configs,
    selectedConfig: expandedConfigId,
    selectedSection,
    selectConfig,
    setSelectedSection,
    createSystem,
    createConfig
  } = useSystemsContext();

  const [systemExpanded, setSystemExpanded] = useState(true);
  const [configExpanded, setConfigExpanded] = useState(true);

  return (
    <Box
      sx={{
        width: "300px",
        borderRight: `1px solid ${theme.palette.divider}`,
        overflowY: "auto",
      }}
    >
      {/* 🔹 SYSTEM SECTION */}
      <Accordion
        expanded={systemExpanded}
        onChange={() => setSystemExpanded((prev) => !prev)}
        disableGutters
        square
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: "none",
          "&:before": { display: "none" },
          m: 0,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "& .MuiAccordionSummary-content": {
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%"
            },
          }}
        >
          <Typography variant="h6">System</Typography>

          {/* ➕ Add System Icon */}

          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation(); // prevent toggling accordion
              createSystem();
            }}
            sx={{
              ml: 1,
              color: theme.palette.status.accent,
              "&:hover": {
                backgroundColor: theme.palette.action.hover,
              },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          <List disablePadding>
            <ListItemButton
              selected={selectedSection === "systemInfo"}
              onClick={() => selectConfig(null)}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: "flex", alignItems: "center" }}>
                    {selectedSection === "systemInfo" && (
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: theme.palette.status.accent,
                          mr: 1,
                        }}
                      />
                    )}
                    <Typography variant="body2">System Info</Typography>
                  </Box>
                }
              />
            </ListItemButton>
          </List>
        </AccordionDetails>
      </Accordion>

      {/* 🔹 CONFIGURATIONS SECTION */}
      <Accordion
        expanded={configExpanded}
        onChange={() => setConfigExpanded((prev) => !prev)}
        disableGutters
        square
        sx={{
          backgroundColor: theme.palette.background.paper,
          boxShadow: "none",
          "&:before": { display: "none" },
          m: 0,
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            "& .MuiAccordionSummary-content": { display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" },
          }}
        >
          <Typography variant="h6">Configurations</Typography>

          {/* ➕ Add Config Icon (future) */}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              createConfig();
            }}
            sx={{
              ml: 1,
              color: theme.palette.status.accent,
              "&:hover": { backgroundColor: theme.palette.action.hover },
            }}
          >
            <AddIcon fontSize="small" />
          </IconButton>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0 }}>
          {configs.map((cfg: any) => {
            const isDefault = cfg.clientProcess?.is_default || cfg.is_default;

            return (
              <Accordion
                key={cfg.name}
                expanded={expandedConfigId === cfg.name}
                onChange={() => selectConfig(cfg.name)}
                disableGutters
                square
                sx={{
                  backgroundColor:
                    expandedConfigId === cfg.name
                      ? theme.palette.action.hover
                      : "inherit",
                  boxShadow: "none",
                  "&:before": { display: "none" },
                  borderLeft:
                    expandedConfigId === cfg.name
                      ? `2px solid ${theme.palette.status.accent}`
                      : "none",
                  m: 0,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{
                    "& .MuiAccordionSummary-content": {
                      minWidth: 0, // 🔥 this is the missing piece
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                      minWidth: 0, // 🔑 allows ellipsis inside flex
                      pr: 1,       // little space before expand icon
                    }}
                  >
                    <Tooltip title={cfg.clientProcess?.name || cfg.name} arrow placement="top">
                      <Typography
                        variant="body2"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          flex: 1,
                          minWidth: 0, // 🔑
                        }}
                      >
                        {cfg.clientProcess?.name || cfg.name}
                      </Typography>
                    </Tooltip>

                    {isDefault && (
                      <StarIcon
                        sx={{ ml: 1, flexShrink: 0, color: theme.palette.status.accent }}
                        fontSize="inherit"
                      />
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ p: 0 }}>
                  <List disablePadding>
                    {tabOptions.map((section) => (
                      <ListItemButton
                        key={section}
                        selected={
                          expandedConfigId === cfg.name &&
                          selectedSection === section
                        }
                        onClick={() => setSelectedSection(section)}
                      >
                        <ListItemText
                          primary={
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              {expandedConfigId === cfg.name &&
                                selectedSection === section && (
                                  <Box
                                    sx={{
                                      width: 8,
                                      height: 8,
                                      borderRadius: "50%",
                                      backgroundColor: theme.palette.status.accent,
                                      mr: 1,
                                    }}
                                  />
                                )}
                              <Typography variant="body2">{section}</Typography>
                            </Box>
                          }
                        />
                      </ListItemButton>
                    ))}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })
          }
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default AccordionPanel;
